import { AttachmentFileType } from 'owa-attachment-file-types';
import initializeAttachmentFullViewState from 'owa-attachment-full-data/lib/actions/initialization/initializeAttachmentFullViewState';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { lazyCreateAttachmentViewState } from 'owa-attachment-well-data';
import { getUserMailboxInfo, ClientAttachmentId } from 'owa-client-ids';
import { FileSuggestion } from 'owa-search-service';
import { lazyAddAndInitializeAttachments, lazyGetAttachment } from 'owa-attachment-model-store';
import { TypeOfAttachment } from 'owa-attachment-type';
import { AttachmentOpenAction, getAttachmentDefaultOpenAction } from 'owa-attachment-data';
import { AttachmentMenuAction, calculateUseJsApi } from 'owa-attachment-full-data';
import isSharePointLinkPreviewable from './isSharePointLinkPreviewable';
import ReferenceAttachment from 'owa-service/lib/contract/ReferenceAttachment';
import loadWacAttachmentInfo from 'owa-attachment-full-data/lib/actions/loadWacAttachmentInfo';
import checkAttachmentPermissionsForWacJsApi from 'owa-attachment-full-data/lib/actions/checkAttachmentPermissionsForWacJsApi';
import { trace } from 'owa-trace';

export default async function isFileImmersiveViewSupported(
    suggestion: FileSuggestion
): Promise<boolean> {
    // is attachment or link?
    if (!!suggestion.AttachmentId) {
        // ID can be in REST format and needs to be converted to EWS format
        const ewsAttachementId = convertRestIdToEwsId(suggestion.AttachmentId);

        const attachmentId: ClientAttachmentId = {
            Id: ewsAttachementId,
            mailboxInfo: getUserMailboxInfo(),
        };

        const [
            createAttachmentViewState,
            getAttachment,
            addAndInitializeAttachmentModels,
        ] = await Promise.all([
            lazyCreateAttachmentViewState.import(),
            lazyGetAttachment.import(),
            lazyAddAndInitializeAttachments.import(),
        ]);

        // Initialize store ViewState and Data for modern attachment items
        // in the broader attachments store
        addAndInitializeAttachmentModels(
            [
                {
                    attachmentId: attachmentId,
                    attachment: suggestion.AttachmentType,
                },
            ],
            true /* isReadOnly */
        );

        const attachment = getAttachment(attachmentId);
        if (!attachment) {
            trace.info(`[isFileImmersiveViewSupported] ${suggestion.FileName} - attachment null`);
            return false;
        }

        const attachmentViewState = createAttachmentViewState(
            attachmentId,
            true, // isReadOnly
            true, // uploadCompleted
            attachment.type === TypeOfAttachment.Reference,
            AttachmentFileType.MailSearchFileSuggestion
        );

        initializeAttachmentFullViewState(
            attachmentViewState,
            attachment,
            false /* treatLinksAsAttachments */,
            attachmentViewState.strategy.supportedMenuActions
        );

        if (attachment.type === TypeOfAttachment.Reference) {
            const providerType: string = (attachment.model as ReferenceAttachment).ProviderType;
            const useJsApi: boolean = calculateUseJsApi(
                attachmentViewState.isCloudy,
                providerType,
                attachment.model.Name
            );

            if (!useJsApi) {
                await loadWacAttachmentInfo(attachmentViewState);
            } else {
                await checkAttachmentPermissionsForWacJsApi(attachment, attachmentViewState);
            }
        }

        const isPreviewSupported = attachmentViewState.strategy.supportedMenuActions.some(
            m => m === AttachmentMenuAction.Preview
        );
        const isDefaultActionPreview =
            getAttachmentDefaultOpenAction(attachmentViewState.strategy) ===
            AttachmentOpenAction.Preview;

        if (isPreviewSupported && isDefaultActionPreview) {
            return true;
        } else {
            trace.info(
                `[isFileImmersiveViewSupported] ${suggestion.FileName} - isPreviewSupported: ${isPreviewSupported}, isDefaultActionPreview: ${isDefaultActionPreview}`
            );
        }
    } else {
        return isSharePointLinkPreviewable(suggestion.FileUrl, suggestion.FileName);
    }

    return false;
}
